// Single source of truth for voucher status presentation — badge variant,
// tint, gradient, icon background, and short description copy. Referenced
// by the list, detail hero, create preview, and redemption log.
//
// Keep Tailwind class names from the Orio360 palette only (no ad-hoc hex).

import type { VoucherStatus } from './types';

export interface VoucherStatusMeta {
  id: VoucherStatus;
  label: string;
  /** Matches @bbpos/ui Badge StatusKey variants. */
  badgeVariant: 'success' | 'voided' | 'warning' | 'draft';
  tint: string;
  darkTint: string;
  iconBg: string;
  gradient: string;
  chip: string;
  textAccent: string;
  description: string;
}

export const VOUCHER_STATUS_META: Record<VoucherStatus, VoucherStatusMeta> = {
  ACTIVE: {
    id: 'ACTIVE',
    label: 'Active',
    badgeVariant: 'success',
    tint: 'bg-success-50',
    darkTint: 'dark:bg-success-900/20',
    iconBg: 'bg-gradient-to-br from-success-500 to-teal-600',
    gradient: 'from-success-500 via-teal-500 to-emerald-600',
    chip: 'bg-success-400/25 text-success-50 ring-1 ring-success-300/40',
    textAccent: 'text-success-700 dark:text-success-300',
    description: 'Redeemable at checkout.',
  },
  EXPIRED: {
    id: 'EXPIRED',
    label: 'Expired',
    badgeVariant: 'voided',
    tint: 'bg-neutral-100',
    darkTint: 'dark:bg-neutral-800/60',
    iconBg: 'bg-gradient-to-br from-neutral-500 to-neutral-700',
    gradient: 'from-neutral-400 via-neutral-500 to-neutral-600',
    chip: 'bg-neutral-400/25 text-neutral-50 ring-1 ring-neutral-300/40',
    textAccent: 'text-neutral-600 dark:text-neutral-300',
    description: 'Past the expiry date. No longer redeemable.',
  },
  EXHAUSTED: {
    id: 'EXHAUSTED',
    label: 'Exhausted',
    badgeVariant: 'warning',
    tint: 'bg-warning-50',
    darkTint: 'dark:bg-warning-900/20',
    iconBg: 'bg-gradient-to-br from-warning-500 to-orange-600',
    gradient: 'from-warning-500 via-orange-500 to-amber-600',
    chip: 'bg-warning-400/25 text-warning-50 ring-1 ring-warning-300/40',
    textAccent: 'text-warning-700 dark:text-warning-300',
    description: 'All redemptions used. Limit reached.',
  },
  INACTIVE: {
    id: 'INACTIVE',
    label: 'Inactive',
    badgeVariant: 'draft',
    tint: 'bg-neutral-100',
    darkTint: 'dark:bg-neutral-800/60',
    iconBg: 'bg-gradient-to-br from-neutral-400 to-neutral-600',
    gradient: 'from-neutral-300 via-neutral-400 to-neutral-500',
    chip: 'bg-neutral-400/25 text-neutral-50 ring-1 ring-neutral-300/40',
    textAccent: 'text-neutral-600 dark:text-neutral-300',
    description: 'Disabled by an admin. Not currently redeemable.',
  },
};

export function statusMeta(status: VoucherStatus | undefined): VoucherStatusMeta {
  return VOUCHER_STATUS_META[status ?? 'INACTIVE'];
}
